import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";

const STATUS_COLOR = {
  awaiting_answer: "bg-[#F2D4D4] text-[#C84B31]",
  answered: "bg-[#0D5057] text-white",
};

export default function AdminDashboard() {
  const [questions, setQuestions] = useState([]);
  const [stats, setStats] = useState(null);
  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState("");
  const [makePublic, setMakePublic] = useState(false);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    try {
      const [q, s] = await Promise.all([api.get("/admin/questions"), api.get("/admin/stats")]);
      setQuestions(q.data);
      setStats(s.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("ak_token");
        nav("/admin/login");
      }
    }
  };

  useEffect(() => { if (!localStorage.getItem("ak_token")) nav("/admin/login"); else load(); }, []);

  const openQ = (q) => {
    setSelected(q);
    setDraft("");
    setMakePublic(q.public || false);
  };

  const aiDraft = async () => {
    if (!selected) return;
    setAiBusy(true);
    try {
      const r = await api.post("/admin/draft", { question_id: selected.id });
      setDraft(r.data.draft);
      toast.success("AI draft ready — edit & send");
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI draft failed");
    } finally {
      setAiBusy(false);
    }
  };

  const send = async () => {
    if (!selected || !draft.trim()) { toast.error("Write an answer first"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/admin/questions/${selected.id}/answer`, { answer: draft, public: makePublic });
      toast.success(r.data.is_follow_up ? "Follow-up sent to " + selected.email : "Answer sent to " + selected.email);
      setDraft("");
      // Reload questions and re-select the just-sent one with updated messages
      const fresh = await api.get("/admin/questions");
      setQuestions(fresh.data);
      const updated = fresh.data.find((x) => x.id === selected.id);
      if (updated) setSelected(updated);
      const s = await api.get("/admin/stats");
      setStats(s.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send");
    } finally {
      setBusy(false);
    }
  };

  const logout = () => { localStorage.removeItem("ak_token"); nav("/admin/login"); };

  return (
    <div className="max-w-7xl mx-auto px-6 py-10" data-testid="admin-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <p className="font-typewriter text-sm tracking-[0.3em] text-[#C84B31] mb-2">/ KIM'S DESK</p>
          <h1 className="font-brush text-6xl">INBOX</h1>
        </div>
        <button data-testid="admin-logout-btn" onClick={logout} className="btn-outline-brutal">LOG OUT</button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
          {[
            { l: "PAID Q's", v: stats.total_paid_questions, c: "bg-[#FDFBF7]" },
            { l: "AWAITING", v: stats.awaiting, c: "bg-[#F2D4D4]" },
            { l: "ANSWERED", v: stats.answered, c: "bg-[#FDFBF7]" },
            { l: "REVENUE", v: `$${stats.revenue_usd.toFixed(0)}`, c: "bg-[#0D5057] text-white" },
          ].map((s, i) => (
            <div key={i} className={`paper-card p-5 ${s.c}`} data-testid={`stat-${s.l.toLowerCase()}`}>
              <p className="font-typewriter text-xs tracking-widest opacity-70">{s.l}</p>
              <p className="font-brush text-5xl mt-1">{s.v}</p>
            </div>
          ))}
        </div>
      )}

      <div className="grid md:grid-cols-12 gap-6 mt-10">
        <div className="md:col-span-5 space-y-4">
          {questions.length === 0 && <p className="font-typewriter text-sm">No paid questions yet.</p>}
          {questions.map((q) => (
            <button
              key={q.id}
              data-testid={`q-item-${q.id}`}
              onClick={() => openQ(q)}
              className={`paper-card w-full text-left p-5 bg-[#FDFBF7] transition-transform hover:-translate-y-0.5 ${selected?.id === q.id ? "ring-2 ring-[#C84B31]" : ""} ${q.urgent && q.status !== "answered" ? "bg-[#F2D4D4]" : ""}`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  {q.urgent && q.status !== "answered" && (
                    <span className="font-brush text-[10px] tracking-widest px-2 py-1 bg-[#C84B31] text-white">URGENT</span>
                  )}
                  <span className={`font-typewriter text-[10px] tracking-widest px-2 py-1 ${STATUS_COLOR[q.status] || "bg-gray-200"}`}>
                    {q.status.toUpperCase()}
                  </span>
                </div>
                <span className="font-typewriter text-xs text-[#4A4A4A]">{new Date(q.created_at).toLocaleDateString()}</span>
              </div>
              <p className="font-cormorant text-xl mt-2 font-semibold">{q.question}</p>
              <p className="font-typewriter text-xs mt-2 text-[#4A4A4A]">{q.name} · {q.email}{q.dog_name ? ` · 🐕 ${q.dog_name}` : ""}</p>
              {q.whatsapp && <p className="font-typewriter text-xs mt-1 text-[#0D5057]">📱 {q.whatsapp}</p>}
            </button>
          ))}
        </div>

        <div className="md:col-span-7">
          {!selected && (
            <div className="paper-card bg-[#FDFBF7] p-10 text-center">
              <p className="font-cormorant text-2xl italic">Pick a question from the inbox →</p>
            </div>
          )}
          {selected && (
            <div className="paper-card bg-[#FDFBF7] p-8" data-testid="admin-answer-panel">
              <p className="font-typewriter text-xs tracking-widest text-[#C84B31]">FROM {selected.name.toUpperCase()} · {selected.email}</p>
              <p className="font-cormorant text-3xl mt-2 font-semibold">{selected.question}</p>
              {(selected.dog_name || selected.dog_breed) && (
                <p className="font-typewriter text-sm mt-2 text-[#4A4A4A]">
                  Dog: {selected.dog_name || "—"} · {selected.dog_breed || "—"}
                </p>
              )}
              {selected.whatsapp && (
                <div className={`mt-3 paper-card p-3 ${selected.urgent ? "bg-[#C84B31] text-white" : "bg-[#FDFBF7]"}`}>
                  <p className="font-brush text-sm tracking-widest">
                    {selected.urgent ? "🚨 URGENT — TEXT THEM" : "📱 WHATSAPP"}
                  </p>
                  <a href={`https://wa.me/${selected.whatsapp.replace(/[^0-9+]/g, "")}`} target="_blank" rel="noreferrer" className="font-typewriter text-base underline">
                    {selected.whatsapp}
                  </a>
                </div>
              )}

              {(() => {
                const history = selected.messages && selected.messages.length > 0
                  ? selected.messages
                  : (selected.answer ? [{ text: selected.answer, sent_at: selected.answered_at, is_follow_up: false }] : []);
                if (history.length === 0) return null;
                return (
                  <div className="mt-5 space-y-3">
                    <p className="font-typewriter text-xs tracking-widest text-[#4A4A4A]">CONVERSATION HISTORY</p>
                    {history.map((m, i) => (
                      <div key={i} className="paper-card bg-white p-4 text-sm">
                        <p className="font-typewriter text-[10px] tracking-widest text-[#0D5057] mb-1">
                          {m.is_follow_up ? "FOLLOW-UP" : "INITIAL REPLY"}
                          {m.sent_at ? ` · ${new Date(m.sent_at).toLocaleString()}` : ""}
                        </p>
                        <p className="font-typewriter text-sm whitespace-pre-wrap text-[#111]">{m.text}</p>
                      </div>
                    ))}
                  </div>
                );
              })()}

              <div className="flex items-center justify-between mt-6">
                <span className="font-typewriter text-xs tracking-widest text-[#4A4A4A]">
                  {selected.status === "answered" ? "FOLLOW-UP MESSAGE" : "YOUR ANSWER"}
                </span>
                <button data-testid="ai-draft-btn" onClick={aiDraft} disabled={aiBusy} className="btn-outline-brutal text-xs py-2">
                  {aiBusy ? "DRAFTING..." : "✦ AI DRAFT"}
                </button>
              </div>
              <textarea
                data-testid="admin-answer-textarea"
                className="textarea-typewriter mt-2 min-h-[260px]"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={selected.status === "answered" ? "Type your follow-up..." : "Hey — first, take a breath..."}
              />

              <label className="flex items-center gap-2 mt-4 font-typewriter text-sm cursor-pointer" style={{display:'none'}}>
                <input type="checkbox" data-testid="admin-public-checkbox" checked={makePublic} onChange={(e) => setMakePublic(e.target.checked)} />
                (legacy public toggle — hidden)
              </label>

              <div className="flex gap-3 mt-6">
                <button data-testid="admin-send-btn" onClick={send} disabled={busy} className="btn-brutal flex-1">
                  {busy ? "SENDING..." : (selected.status === "answered" ? "SEND FOLLOW-UP →" : "SEND ANSWER →")}
                </button>
                <button data-testid="admin-clear-btn" onClick={() => setSelected(null)} className="btn-outline-brutal">CANCEL</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
